import styled, { css } from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Bugsnag from '@bugsnag/js';

import { Tabs } from 'checkout/components/Tabs/Tabs';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { Loader } from 'shared/components/Loader';
import { useLocation } from 'shared/hooks/useLocation';
import { ITabName } from 'checkout/types/Tab';
import { ErrorBoundary } from 'checkout/components/ErrorBoundary';
import { logCartCheckout, logEcomPurchase } from 'tracking/utils/eCommerceEvents';
import { PaymentType } from 'shared/enums/PaymentType';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import { variableFontRegular } from 'styles/theme/text';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { ErrorMessageModal } from 'checkout/components/ErrorMessageModal';
import { OrderSummary } from 'checkout/components/Summary/OrderSummary';
import { useCurrency } from 'shared/hooks/useCurrency';
import { fireTiktokCheckoutPlaceOrder } from 'tracking/utils/tiktokEvents';
import { GiftCardSummary } from 'checkout/components/Summary/GiftCardSummary';
import { GiftCardEventTypes } from 'gift-cards/enums/constants';
import { guestPurchasingGiftCard, purchasingGiftCard } from 'checkout/utils/giftCards';
import { useStoreSelector } from 'store/hooks/storeHooks';

interface IAfterPayGlobal {
  initialize: ({ countryCode }: { countryCode: string }) => void;
  redirect: ({ token }: { token: string }) => void;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'afterpay-placement': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
  interface Window {
    AfterPay: IAfterPayGlobal;
  }
}

export const Checkout = () => {
  const {
    addresses,
    billingInfos,
    changeTab,
    connectBillingInfoToOrder,
    createBillingInfo,
    loading,
    order,
    orderSubmitting,
    purchaseOrder,
    isGuestCheckout,
  } = useCheckoutContext();
  const { isAuthenticated, user } = useStoreSelector((state) => state.auth);
  const { selectedCurrencyCode } = useCurrency();
  const { selectedCountryCode } = useLocation();
  const [isAutoPurchasing, setIsAutoPurchasing] = useState(false);
  const router = useRouter();

  const forceShowShippingTabEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_FORCE_SHOW_SHIPPING_TAB);
  const isCheckoutPurchaseGiftCardsEnabled = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_PURCHASE_GIFT_CARDS,
  );
  const isCheckoutOffersEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_OFFERS);
  const isTiktokEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_TIKTOK);
  const isWebEnableCheckoutGiftCardsAllowGuestPurchase = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_GIFT_CARDS_ALLOW_GUEST_PURCHASE,
  );

  const isCheckoutCompleted = order?.status !== 'pending';
  const isGuestPurchasingGiftCard = guestPurchasingGiftCard({
    giftCardId: router?.query?.giftCardId,
    isAuthenticated,
    isGuestCheckout,
    isWebEnableCheckoutGiftCardsAllowGuestPurchase,
  });
  const isPurchasingGiftCard = purchasingGiftCard({
    giftCardId: order?.product?.giftCardId,
    isGuestPurchasingGiftCard,
    isCheckoutPurchaseGiftCardsEnabled,
  });

  const isOfferReady = !loading.want && isCheckoutOffersEnabled;
  const isOrderReady = !loading.address && !loading.billingInfo && !loading.order;

  const isReady = useRef<boolean>(false);

  let giftCardType = GiftCardEventTypes.NULL;
  if (order?.product?.giftCardId) {
    giftCardType = GiftCardEventTypes.PURCHASED;
  }
  if (order?.giftCards?.length > 0) {
    giftCardType = GiftCardEventTypes.APPLIED;
  }

  useEffect(() => {
    if (isOrderReady) {
      const isOffer = order?.wantId && isCheckoutOffersEnabled;

      isReady.current = isOffer ? isOrderReady && isOfferReady : isOrderReady;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrderReady]);

  useEffect(() => {
    const purchaseAffirmOrAfterpayOrder = async (tokenParam: {
      affirmCheckoutToken?: string;
      afterpayOrderToken?: string;
    }) => {
      changeTab(ITabName.REVIEW);
      setIsAutoPurchasing(true);

      try {
        let paymentType;

        if (tokenParam.affirmCheckoutToken) {
          paymentType = PaymentType.Affirm;
        } else if (tokenParam.afterpayOrderToken) {
          paymentType = PaymentType.AfterPay;
        }

        const billingInfo = await createBillingInfo(tokenParam, paymentType);

        await connectBillingInfoToOrder(billingInfo?.id);

        changeTab(ITabName.REVIEW);

        await purchaseOrder({ paymentType });

        logEcomPurchase(
          {
            name: order?.product?.productTemplate?.name,
            sku: order?.product?.productTemplate?.sku,
            priceCents: order?.product?.localizedPriceCents?.amountUsdCents,
            brandName: order?.product?.productTemplate?.brandName,
            condition: order?.product?.shoeCondition,
          },
          order,
        );

        sendTrackingEvent('CART_PURCHASE', {
          content_type: 'product_group',
          currency_code: selectedCurrencyCode,
          goat_storage: false,
          instant_ship: order?.isInstantShip,
          is_apparel_product: order?.product?.productTemplate?.isFashionProduct,
          order_id: order?.id,
          order_number: order?.number,
          price: order?.localizedPriceCents?.amount,
          product_template_slug: order?.product?.productTemplate?.slug,
          shipping_method: order?.shippingServiceLevel,
          shipping_price: order?.shippingCents,
          size: order?.product?.size,
          subtotal_price_dollars: ((order?.localizedPriceCents?.amountUsdCents || 0) / 100).toFixed(
            2,
          ),
          subtotal_price: order?.localizedPriceCents?.amount,
          gift_card_type: giftCardType,
        });

        if (isTiktokEnabled) {
          fireTiktokCheckoutPlaceOrder(
            {
              contentCategory: order?.product?.productTemplate?.brandName,
              contentId: order?.product?.productTemplate?.sku,
              currency: selectedCurrencyCode,
              price: order?.localizedPriceCents?.amount,
              quantity: 1,
              value: order?.localizedFinalPriceCents?.amount,
            },
            user?.email,
          );
        }
        setIsAutoPurchasing(false);
      } catch (error) {
        setIsAutoPurchasing(false);
        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber: order?.number });
        });
      }
    };

    if (isReady.current) {
      /**
       * These webhooks for some payment methods used to be processed on the GQL layer.
       * Since we no longer have a dedicated backend for our frontend, we're processing it here.
       */
      const urlSearchParams = new URLSearchParams(window.location.search);
      const params = Object.fromEntries(urlSearchParams.entries());

      if (isCheckoutCompleted) {
        void router.replace('/');
        return;
      }

      // Affirm webhook
      if (params.checkout_token && params.status !== 'CANCELLED') {
        void purchaseAffirmOrAfterpayOrder({ affirmCheckoutToken: params.checkout_token });

        return;
      }

      // Afterpay webhook
      if (params.orderToken && params.status !== 'CANCELLED') {
        void purchaseAffirmOrAfterpayOrder({ afterpayOrderToken: params.orderToken });

        return;
      }

      // this part figures out if any parts of the checkout flow can be skipped from
      // the data saved from previous checkouts
      const shippingAddress = addresses[order?.addressId];
      const billingInfo = billingInfos[order?.billingInfoId];

      const isOffer = order?.wantId && isCheckoutOffersEnabled;

      const isAffirm = order?.paymentType === PaymentType.Affirm;
      const isAfterPay = order?.paymentType === PaymentType.AfterPay;
      const isCreditCard = order?.paymentType === PaymentType.CreditCard;

      const isMatchingShippingRegion = shippingAddress?.countryCode === selectedCountryCode;
      const isShippingToUS = shippingAddress?.countryCode === 'US';

      const isValidAffirm =
        isAffirm && isMatchingShippingRegion && isShippingToUS && !!order?.billingInfoId;
      const isValidAfterPay =
        isAfterPay && isMatchingShippingRegion && isShippingToUS && !!order?.billingInfoId;
      const isValidCreditCard = isCreditCard && isMatchingShippingRegion && !!order?.billingInfoId;

      const hasManyAvailableShippingMethods =
        order?.shippingServiceLevelOptions.filter((option) => option.available)?.length > 1;

      const isForceShowingMultipleShipping =
        forceShowShippingTabEnabled && hasManyAvailableShippingMethods;

      switch (true) {
        case isValidAffirm && !isOffer:
        case isValidAfterPay && !isOffer:
        case isValidCreditCard && !isForceShowingMultipleShipping:
          changeTab(ITabName.REVIEW);
          break;

        case isMatchingShippingRegion && !order?.billingInfoId && !isForceShowingMultipleShipping:
        case isMatchingShippingRegion && !billingInfo && isOffer:
        case !billingInfo && isPurchasingGiftCard:
          changeTab(ITabName.PAYMENT);
          break;

        case isMatchingShippingRegion &&
          !order?.billingInfoId &&
          isForceShowingMultipleShipping &&
          !isPurchasingGiftCard:
        case isValidCreditCard && isForceShowingMultipleShipping && !isPurchasingGiftCard:
        case !isMatchingShippingRegion && !isPurchasingGiftCard:
          changeTab(ITabName.SHIPPING);
          break;

        default:
          break;
      }

      logCartCheckout({
        name: order?.product?.productTemplate?.name,
        sku: order?.product?.productTemplate?.sku,
        priceCents: order?.product?.localizedPriceCents?.amountUsdCents,
        brandName: order?.product?.productTemplate?.brandName,
        condition: order?.product?.shoeCondition,
      });
    }
  }, [isReady.current]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Checkout.Wrapper $loading={!isOrderReady}>
      <ErrorBoundary>
        {isOrderReady && !isCheckoutCompleted ? (
          <>
            <Checkout.Row>
              <Checkout.Col>
                <Tabs />
              </Checkout.Col>
              <Checkout.Col>
                {isPurchasingGiftCard ? <GiftCardSummary /> : <OrderSummary />}
              </Checkout.Col>
            </Checkout.Row>
            {(isAutoPurchasing || orderSubmitting) && (
              <Checkout.BlockingOverlay data-qa="blocking_overlay" />
            )}
          </>
        ) : (
          <Loader style={{ width: '70%', margin: 'auto' }} />
        )}
      </ErrorBoundary>
      <ErrorMessageModal />
    </Checkout.Wrapper>
  );
};

Checkout.Wrapper = styled.div<{ $loading: boolean }>`
  background: white;
  min-height: calc(100vh - 216px);
  width: 100%;

  ${({ $loading }) =>
    $loading &&
    css`
      display: flex;
    `}

  ${variableFontRegular({ size: '11.5px' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    padding: 0 32px;
  }
`;

/* Blocks user interaction while order is submitting */
Checkout.BlockingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndex.blockingOverlay};
`;

Checkout.Row = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    flex-direction: row;
  }
`;

Checkout.Col = styled.div`
  width: 100%;
  max-width: 428px;
  margin-top: 40px;

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    width: 50%;
    margin-top: 24px;
  }
`;
